import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const json = require("../../../config.json");
const urlBase = json.urlBase;

export const OAUTH_SESSION_URL = urlBase + "/oauth/authorize/session";
export const OAUTH_AUTHORIZE_URL = urlBase + "/oauth/authorize";

// No interceptors: the shared axios instance force-redirects to /auth/login on
// any 401, which would swallow the "your session is not usable" answer that
// this page exists to react to.
const oauthClient = axios.create();

/**
 * Landing page for the OAuth authorization server's "are you already signed in
 * here?" check.
 *
 * api.itpscorp.com/.../oauth/authorize cannot see the CRM session, because the
 * session lives in this origin's localStorage. So for a trusted client it
 * sends the browser here with `req`, its own signature over the already
 * validated authorize request, and this page hands the stored token back.
 *
 * Two outcomes, and nothing else:
 *
 *   - a usable session: POST {req, token}, then follow the redirect the API
 *     returns, which is the client's own callback with an authorization code.
 *   - anything else: go back to the authorize endpoint with `prompt=login` so
 *     the ordinary password page renders.
 *
 * The bounce back carries `req` and nothing this page made up, so there is no
 * URL here an attacker could steer. The API refuses to send a browser here a
 * second time for the same request (the marker is inside that signature), so
 * the fallback cannot ping-pong.
 */
export default function OAuthContinue() {
  const startedRef = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    const req = new URLSearchParams(window.location.search).get("req");
    if (!req) {
      // Nothing to continue. Never guess an authorize URL from here.
      setFailed(true);
      return;
    }

    const fallback = () => {
      window.location.replace(
        OAUTH_AUTHORIZE_URL + "?req=" + encodeURIComponent(req) + "&prompt=login"
      );
    };

    const token = storedSessionToken();
    if (!token) {
      fallback();
      return;
    }

    oauthClient
      .post(OAUTH_SESSION_URL, { req, token })
      .then((response) => {
        const target = response?.data?.result?.redirect;
        if (target) {
          window.location.replace(target);
        } else {
          fallback();
        }
      })
      .catch(fallback);
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.title}>
          {failed ? "Sign-in link is incomplete" : "Signing you in..."}
        </div>
        <div style={styles.body}>
          {failed
            ? "Start the sign-in from the app again."
            : "Checking your CRM session."}
        </div>
      </div>
    </div>
  );
}

function storedSessionToken() {
  try {
    const details = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const fromDetails = String(details.access_token || "").replace(/['"]+/g, "");
    if (fromDetails) {
      return fromDetails;
    }
  } catch (e) {
    // Unparsable localStorage is the same as no session.
  }
  try {
    const stored = JSON.parse(localStorage.getItem("persist:demo4-auth") || "{}");
    return String(stored.authToken || "").replace(/['"]+/g, "");
  } catch (e) {
    return "";
  }
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0b1220",
    color: "#e2e8f0",
    font: '14px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  card: {
    width: "100%",
    maxWidth: 380,
    background: "#111a2e",
    border: "1px solid #1e293b",
    borderRadius: 12,
    padding: 28,
    textAlign: "center",
  },
  title: { fontSize: 17, fontWeight: 600, color: "#f1f5f9", marginBottom: 6 },
  body: { fontSize: 13, color: "#94a3b8" },
};
